import { UserModel } from '@features/user/models/user.model';
import { Entities } from '@core/components/entities.enum';
import { PageConfig } from '@core/models/page-config.model';
import {
  Component,
  ElementRef,
  Injector,
  OnInit,
  OnDestroy,
  ViewChild,
  Renderer2,
  ViewEncapsulation,
  HostBinding,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PageRoute } from '@core/enums/general/routes.enum';
import { UrlParams } from '@core/enums/url-params.enum';
import { UserService } from '@features/user/services/user.service';
import { AuthService } from '@features/auth/services/auth.service';
import { ModuleService } from '@features/module/services/module.service';
import { TranslateService } from '@ngx-translate/core';
import { IntBaseListComponent } from '@src/app/shared/base/int-base-list.component';
import { Modules } from '@core/enums/modules.enum';
import { LegacyKeyResponseInterface } from '@core/models/legacy-key-response.interface';

@Component({
  selector: 'app-legacy',
  templateUrl: './legacy.component.html',
  styleUrls: ['./legacy.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class LegacyComponent extends IntBaseListComponent<any> implements OnInit, OnDestroy {
  @HostBinding('class')
  public class = 'legacy-component';

  public url: SafeResourceUrl;
  public currentUrlString: string[];
  public height = '0px';
  @ViewChild('legacyIframe', { static: true })
  public legacyIframe: ElementRef;
  public currentUser: UserModel;

  public loading = true;
  protected moduleName: string;
  protected portalAliases = [
    'manage',
  ];
  private messageEventHandler: (event: MessageEvent) => void;
  private legacyResponseData: LegacyKeyResponseInterface;
  private resyncAttempted = false;
  private endpointOverride: string | null = null;
  private static readonly RESYNC_PROPAGATION_DELAY_MS = 4000;

  constructor(
    public injector: Injector,
    public route: ActivatedRoute,
    public router: Router,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private authService: AuthService,
    public moduleService: ModuleService,
    public translateService: TranslateService,
    private _renderer: Renderer2,
  ) {
    super(injector);
    this.currentUrlString = this.router.url.split('?')[0].split('/');
    this.moduleName = this.currentUrlString[1];

    if (this.moduleName === Modules.Intergrip) {
      this.moduleName = this.currentUrlString.indexOf('_vombo') > -1 ? 'overstap' : 'portal';
    }

    if (this.router.url.includes(`${Entities.ImportStudent}`)) {
      this.moduleName = 'upload';
    }

    this.endpointOverride = this.route.snapshot.queryParamMap.get(UrlParams.Endpoint);

    this.userService.legacyKey(this.moduleName).subscribe(response => {
      this.legacyResponseData = response.data;

      if (this.endpointOverride) {
        this.url = `${response.data.legacyBaseUrl}/auth/legacy/key?endpoint=${encodeURIComponent(this.endpointOverride)}&key=${response.data.legacyKey}`;
      } else {
        this.setUrl(response.data.legacyKey, response.data.legacyBaseUrl, this.route.routeConfig.path);
      }

      this.setupMessageListener();
    });
  }

  private setupMessageListener(): void {
    if (this.messageEventHandler) {
      window.removeEventListener('message', this.messageEventHandler);
    }

    this.messageEventHandler = (event: MessageEvent) => {
      this.handleRoleUnsyncedError(event, this.legacyResponseData);
      this.handlePotentialLogout(event, this.legacyResponseData);
      this.handleHeightUpdate(event, this.legacyResponseData);
      this.handleScrollTop(event, this.legacyResponseData);
      this.handleScrollBottom(event, this.legacyResponseData);
      this.redirectToMentor(event, this.legacyResponseData);
      this.handleScrollToImage(event, this.legacyResponseData);
    };

    window.addEventListener('message', this.messageEventHandler);
  }

  ngOnDestroy(): void {
    if (this.messageEventHandler) {
      window.removeEventListener('message', this.messageEventHandler);
    }
  }

  private handleRoleUnsyncedError(event: MessageEvent, response: LegacyKeyResponseInterface): void {
    if (
      this.normalizeOrigin(event.origin) === this.normalizeOrigin(response.legacyBaseUrl)
      && event.data === 'role_unsynced'
    ) {
      this.attemptRoleResyncThenReload();
    }
  }

  public handleScrollTop(event: MessageEvent, response: LegacyKeyResponseInterface): void {
    if (event.data === 'scrollToTop' || (typeof event.data !== 'object' && event.data.indexOf('popup') > -1)) {
      document.querySelector('.page-wrapper').scrollTo(0, 0);
    }
  }

  public handleScrollBottom(event: MessageEvent, response: LegacyKeyResponseInterface): void {
    if (event.data === 'scrollToBottom') {
        document.querySelector('.page-wrapper').scrollTo(0, 99999);
    }
  }

  public redirectToMentor(event: MessageEvent, response: LegacyKeyResponseInterface): void {
    if (event.data === 'redirectToMentor') {
        this.router.navigate(['/manage/student/mentor/overview']);
    }
  }

  public handleScrollToImage(event: MessageEvent, response: LegacyKeyResponseInterface): void {
    if ((typeof event.data !== 'object' && event.data?.indexOf('scrollToY.') > -1)) {
      const yCord = parseInt(event.data.split('.')[1], 10);
      document.querySelector('.page-wrapper').scrollTo(0, yCord);
    }
  }

  public handlePotentialLogout(event: MessageEvent, response: LegacyKeyResponseInterface): void {
    if (
      this.normalizeOrigin(event.origin) === this.normalizeOrigin(response.legacyBaseUrl)
      && event.data === 'logout'
    ) {
      this.attemptRoleResyncThenReload();
    }
  }

  private attemptRoleResyncThenReload(): void {
    if (this.resyncAttempted) {
      this.redirectToMainPageWithError();

      return;
    }

    this.resyncAttempted = true;
    this.loading = true;

    this.toastService.warning(
      this.translateService.instant('toast.warning.title'),
      this.translateService.instant('intergrip.errors.legacy.role-syncing'),
    );

    this.userService.resyncLegacyRoleUser(this.moduleName).subscribe({
      next: () => {
        setTimeout(() => {
          this.userService.legacyKey(this.moduleName).subscribe({
            next: (response) => {
              this.legacyResponseData = response.data;

              if (this.endpointOverride) {
                this.url = `${response.data.legacyBaseUrl}/auth/legacy/key?endpoint=${encodeURIComponent(this.endpointOverride)}&key=${response.data.legacyKey}`;
              } else {
                this.setUrl(response.data.legacyKey, response.data.legacyBaseUrl, this.route.routeConfig.path);
              }
            },
            error: () => this.redirectToMainPageWithError(),
          });
        }, LegacyComponent.RESYNC_PROPAGATION_DELAY_MS);
      },
      error: () => this.redirectToMainPageWithError(),
    });
  }

  private redirectToMainPageWithError(): void {
    this.toastService.error(
      this.translateService.instant('toast.error.title'),
      this.translateService.instant('intergrip.errors.legacy.something-went-wrong'),
    );

    this.router.navigate(['/']);
  }

  private normalizeOrigin(value: string): string {
    return (value || '').replace(/\/+$/, '');
  }

  public handleHeightUpdate(event: MessageEvent, response: LegacyKeyResponseInterface): void {
    if (
      event.origin.indexOf('intergrip')
      && event.data !== undefined
      && typeof event.data === 'string'
      && event.data.indexOf('scrollHeight.') > -1
    ) {
      const newHeight = parseInt(event.data.split('.')[1], 10);
      if (event.data.indexOf('popup') === -1) {
        this.height = `${newHeight}px`;
      }
    }
  }

  public ngOnInit(): void {
    super.ngOnInit();

    this._renderer.listen(this.legacyIframe.nativeElement, 'load', () => {
      if (!this.url) {
        return;
      }
      setTimeout(() => {
        this.loading = false;
      }, 300);

      if (this.legacyResponseData) {
        this.sendPortalContext();
      }
    });
  }

  private sendPortalContext(): void {
    try {
      const portalPath = this.router.url.split('?')[0];
      this.legacyIframe.nativeElement.contentWindow.postMessage({
        type: 'portalContext',
        portalBaseUrl: window.location.origin,
        portalPath,
      }, '*');
    } catch (e) {
      // Cross-origin error — silently ignore
    }
  }

  public getDefaultConfig(): PageConfig {
    const pageTitleOverride = this.route.snapshot.queryParamMap.get(UrlParams.PageTitle);
    if (pageTitleOverride) {
      const backUrl = `/${this.currentUrlString[1]}/${this.currentUrlString[2]}`;
      return this.currentUrlString?.length >= 4
        ? { title: pageTitleOverride, backUrl }
        : { title: pageTitleOverride };
    }

    let key = `intergrip.${this.currentUrlString[1]}.${this.currentUrlString[2]}.overview.title`;
    if (this.currentUrlString?.length >= 4) {
      let backUrl = `/${this.currentUrlString[1]}/${this.currentUrlString[2]}`;

      switch (true) {
        case this.router.url.includes(Entities.myStudents):
          key = `intergrip.${this.currentUrlString[1]}.student.overview.title`;
          backUrl = ``;
          break;

        case this.router.url.includes('student/education_day'):
          key = `intergrip.${this.currentUrlString[1]}.search.overview.title`;
          backUrl = ``;
          break;

        case this.router.url.includes(`${Modules.LobActivities}/${Entities.activityOverview}`):
          key = `${this.currentUrlString[1]}.menu.activities.title`;
          break;

        case this.router.url.includes(`${Modules.LobActivities}/${Entities.Settings}`):
          key = `${this.currentUrlString[1]}.menu.settings.title`;
          break;

        case this.router.url.includes(`${Modules.LobActivities}/${Entities.registrations}`):
          key = `${this.currentUrlString[1]}.menu.registrations.title`;
          break;

        case this.router.url.includes(`${Modules.LobActivities}/${Entities.student}`):
          key = `${this.currentUrlString[1]}.menu.students.title`;
          break;

        case this.router.url.includes(`${Modules.LobActivities}/${Entities.intergrip_students}`):
          key = `${this.currentUrlString[1]}.menu.all-students.title`;
          backUrl = ``;
          break;

        case this.router.url.includes(`${Modules.LobVoorlichtingen}/${Entities.lobVoorlichtingenActivities}`):
          backUrl = ``;
          break;

        case this.router.url.includes(`${Modules.Ddd}/${Entities.voDossiers}/${Entities.dossier}`):
          backUrl = ``;
          break;

        case this.router.url.includes(`${Modules.LobActivities}/${Entities.myStudentTab}`):
          key = `${this.currentUrlString[1]}.menu.my-students.title`;
          backUrl = `/${Modules.LobActivities}/${Entities.myStudentTab}`;
          break;

        case this.router.url.includes(`${Entities.intergrip1Students}`) &&
        this.router.url.includes(`${Entities.Students}`) && !this.router.url.includes('student-portal'):
          key = `${this.currentUrlString[1]}.student.overview.title`;
          break;

        case this.router.url.includes(`${Modules.Intergrip}/${Entities.Instances}/${Entities.location}`):
          key = `${this.currentUrlString[1]}.instances.overview.title`;
          break;

        case this.router.url.includes(`${Entities.Communications},${Entities.ContentNotificationsSuper}`):
          key = 'intergrip.sub-menu.content-notifications.title';
          break;

        case this.router.url.includes(`${Entities.Communications},${Entities.ContentNotificationsAdmin}`):
          key = 'intergrip.sub-menu.content-notifications.title';
          break;

        case this.router.url.includes(`${Modules.Intergrip}/${Entities.Students}`):
          key = `${this.currentUrlString[1]}.menu.students`;
          break;

        case this.router.url.includes(Entities.CreboList1):
          key = 'intergrip.crebo-list-1.overview.title';
          break;

        case this.router.url.includes(`${Modules.Intergrip}/${Entities.MboCheck}`):
          key = `transfer-vo.menu.mbo-checks`;
          break;

        case this.router.url.includes(`${Modules.Intergrip}/${Entities.intergrip1Manage}`):
          key = `intergrip.menu.settings`;
          backUrl = ``;
          break;

        case this.router.url.includes(`${Modules.Ddd}/${Entities.voReports}`):
          key = `${this.currentUrlString[1]}.menu.reports.title`;
          break;

        case this.router.url.includes(`${Modules.Ddd}/${Entities.specialTransferManage}`):
          key = `${this.currentUrlString[1]}.${Entities.specialTransfer}.title`;
          backUrl = ``;
          break;

        case this.router.url.includes(`${Entities.eld}`):
          key = `${this.currentUrlString[1]}.${Entities.eld}.overview.title`;
          break;

        case this.router.url.includes(`${Entities.int1connections}`):
          key = `${this.currentUrlString[1]}.${Entities.int1connections}.overview.title`;
          break;

        case this.router.url.includes(`${Entities.acties}`):
          key = `${this.currentUrlString[1]}.${Entities.acties}.overview.title`;
          break;

        case this.router.url.includes(`${Entities.fixes}`):
          key = `${this.currentUrlString[1]}.${Entities.fixes}.overview.title`;
          break;

        case this.router.url.includes(`${Entities.int1export}`) && this.router.url.includes(`${Entities.reports}`):
          key = `${this.currentUrlString[1]}.${Entities.reports}.overview.title`;
          break;

        case this.router.url.includes(`${Entities.int1export}`) && this.router.url.includes(`${Entities.intergrip1Monitoring}`):
          key = `${this.currentUrlString[1]}.${Entities.intergrip1Monitoring}.overview.title`;
          break;

        case this.router.url.includes(`${Entities.int1export}`):
          key = `${this.currentUrlString[1]}.exports.overview.title`;
          backUrl = ``;
          break;

        case this.router.url.includes(`${Entities.int1Settings}`):
          key = `${this.currentUrlString[1]}.int1-settings.overview.title`;
          backUrl = ``;
          break;

        case this.router.url.includes(`${Entities.intergrip1Monitoring}`):
          key = this.route.snapshot.data?.title ?? `${this.currentUrlString[1]}.${Entities.intergrip1Monitoring}.overview.title`;
          break;

        case this.router.url.includes(`${Entities.reports}`):
          key = `${this.currentUrlString[1]}.${Entities.reports}.overview.title`;
          break;

        case this.router.url.includes(`${Entities.admin}`):
          key = `${this.currentUrlString[1]}.${Entities.admin}.overview.title`;
          break;

        case this.router.url.includes(`${Entities.TodoStudents}`):
          key = `${this.currentUrlString[1]}.${Entities.TodoStudents}.overview.title`;
          break;
        case this.router.url.includes(`${Entities.Setting}/${Entities.System}`):
          key = `${this.currentUrlString[1]}.${this.currentUrlString[2]}.overview.title`;
          backUrl = ``;
          break;
        case this.router.url.includes(`${Entities.Setting}/${Entities.Vo}`):
        case this.router.url.includes(`${Entities.Setting}/${Entities.Region}`):
          key = `${this.currentUrlString[1]}.${this.currentUrlString[2]}.${this.currentUrlString[3]}.overview.title`;
          break;
        case this.router.url.includes(`${Entities.Setting}/${Entities.Choice}`):
          key = `${this.currentUrlString[1]}.${this.currentUrlString[2]}.mbo.overview.title`;
          break;

        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.lobManagement}/${Entities.competences}`):
          key = `lobdossier.sub-menu.management-competences.title`;
          break;

        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.lobManagement}/${Entities.format}`):
          key = `lobdossier.sub-menu.management-format.title`;
          break;

        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.lobManagement}/${Entities.managementRegion}`):
          key = `lobdossier.sub-menu.management-region.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.lobManagement}/${Entities.managementMbo}`):
          key = `lobdossier.sub-menu.management-mbo.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.lobManagement}/${Entities.managementVo}`):
          key = `lobdossier.sub-menu.management-vo.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.assignment}/${Entities.assignmentOverview}`):
          key = `lobdossier.sub-menu.assignment-overview.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.assignment}/${Entities.assignmentRegionAll}`):
          key = `lobdossier.sub-menu.assignment-region-all.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.assignment}/${Entities.assignmentGeneralArchived}`):
          key = `lobdossier.sub-menu.assignment-general-archived.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.lobAssignment}/${Entities.assignmentAll}`):
          key = `lobdossier.sub-menu.lob-assignment-all.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.lobAssignment}/${Entities.assignmentReceived}`):
          key = `lobdossier.sub-menu.lob-assignment-recieved.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.lobAssignment}/${Entities.assignmentShared}`):
          key = `lobdossier.sub-menu.lob-assignment-shared.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.lobAssignment}/${Entities.assignmentOverview}`):
          key = `lobdossier.sub-menu.lob-assignment-overview.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.lobAssignment}/${Entities.assignmentArchived}`):
          key = `lobdossier.sub-menu.lob-assignment-archived.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.carrierAssignment}/${Entities.carrierAssignmentAll}`):
          key = `lobdossier.sub-menu.carrier-assignment-all.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.carrierAssignment}/${Entities.carrierAssignmentPreparation}`):
          key = `lobdossier.sub-menu.carrier-assignment-preparation.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.InternshipAssignments}/${Entities.InternshipAssignmentsAssignments}`):
          key = `internship-assignments.sub-menu.internship-assignments-assignments.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.InternshipAssignments}/${Entities.InternshipAssignmentsArchivedAssignments}`):
           key = `internship-assignments.sub-menu.internship-assignments-archived-assignments.title`;
           break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.handedInAssignments}/${Entities.handedPerAssignment}`):
           key = `lobdossier.sub-menu.handed-in-assignments-assignment.title`;
           break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.handedInAssignments}/${Entities.handedPerStudent}`):
          key = `lobdossier.sub-menu.handed-in-assignments-student.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.carrierAssignment}/${Entities.carrierAssignmentEvaluation}`):
          key = `lobdossier.sub-menu.carrier-assignment-evaluation.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.ChoiceSemester}/${Entities.ChoiceSemestersAssignments}`):
          key = `lobdossier.sub-menu.choice-semester-assignments.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.ChoiceSemester}/${Entities.ChoiceSemesterArchivedAssignments}`):
          key = `lobdossier.sub-menu.choice-semester-archived-assignments.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.ChoiceSemester}/${Entities.ChoiceSemestersOverview}`):
          key = `lobdossier.sub-menu.choice-semester-overview.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.TaskList}/${Entities.TaskPeriod}`):
          key = `lobdossier.sub-menu.task-list-periods.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.TaskList}/${Entities.TaskPart}`):
          key = `lobdossier.sub-menu.task-list-parts.title`;
          break;
        case this.router.url.includes(`${Modules.Lobdossier}/${Entities.TaskList}/${Entities.Tasks}`):
          key = `lobdossier.sub-menu.task-list-tasks.title`;
          break;
        default:
          backUrl = `/${this.currentUrlString[1]}/${this.currentUrlString[2]}`;
      }

      if (this.router.url.includes(`${Entities.lobDossier}/${Entities.lobdossierMyStudents}`)) {
        key = `${this.currentUrlString[1]}.${Entities.Students}.overview.title`;
        backUrl = ``;
      }
      return {
        title: key ? this.translateService.instant(key) : '',
        backUrl,
      };
    }

    if (this.router.url.includes(`${Entities.SystemPreference}`)) {
      key = '';
    }

    return {
      title: key ? this.translateService.instant(key) : '',
    };
  }

  private setUrl(key: string, baseUrl: string, url: string): void {
    if (this.router.url.includes(`${Modules.LobActivities}/${Entities.activityOverview}/status`)) {
      url = `${Entities.activityOverview}/${url}`;
    }
    if (url === 'location-mbo') {
      url = `/_portal/admin/location`;
    }
    if (this.router.url.includes(Entities.SystemPreference)) {
      url = 'settings/system';
    }
    this.url = `${baseUrl}/auth/legacy/key?endpoint=${url}&key=${key}`;
  }
}
